<template>
  <div class="col-xs-12">
    <steps :filled-steps="filledSteps" />
    <h2 class="title-2 text-center partner-title">
      Personal Impact Information
    </h2>

    <form @submit.prevent="handleSubmit" class="form-container" novalidate>
      <div class="grid">
        <div class="grid-1">
          <div class="input-group">
            <label>Title of your Statement</label>
            <input
              type="text"
              v-model="form.titleStatement"
              class="input-field"
              @input="$v.form.titleStatement.$touch()"
              :class="{
                'success': (!$v.form.titleStatement.$invalid && $v.form.titleStatement.$dirty) || form.titleStatement,
                'warning': $v.form.titleStatement.$invalid && $v.form.titleStatement.$dirty
              }"
              required
            >

            <span v-show="!$v.form.titleStatement.$invalid" class="span-state success">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>

            <transition name="fade">
              <warning-alert v-if="$v.form.titleStatement.$error">
                This field is required
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label>
              In 500 words or less, please share with us the positive impact that receiving school supplies would have on your classroom and students.
            </label>

            <textarea
              v-model="form.statement"
              class="input-field"
              @input="$v.form.statement.$touch()"
              :class="{
                'success': (!$v.form.statement.$invalid && $v.form.statement.$dirty) || form.statement,
                'warning': $v.form.statement.$invalid && $v.form.statement.$dirty
              }"
              required
            ></textarea>
            <transition name="fade">
              <warning-alert v-if="$v.form.statement.$error">
                This field is required
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label>
              May we use your impact statement in other materials?
            </label>

            <div class="d-block">
              <div class="input-radio">
                <input type="radio" :value="true" v-model="form.useImpactStatementMaterial" >
                <span>Yes</span>
                <!-- <span class="inner-circle" :class="{ 'filled-background': form.useImpactStatementMaterial }"></span> -->
              </div>
              <div class="input-radio">
                <input type="radio" :value="false" v-model="form.useImpactStatementMaterial">
                <span>No</span>
                <!-- <span class="inner-circle" :class="{ 'filled-background': !form.useImpactStatementMaterial }"></span> -->
              </div>
            </div>
          </div>

          <div class="input-group">
            <label>
              <b>
                Choose photos that will help your profile stand out!
              </b>
            </label>
            <p>
              Choose photos that will help your profile stand out! This could be a picture of you in your
              classroom, working alongside your students or another appropiate photo. If you choose to use an
              image featuring your students, your uploading affirms that you have permission to share their
              likeness(es).
            </p>
          </div>

          <section class="pictures-grid">
            <div class="input-group">
              <label>
                Upload your cover photo
                <button class="show-help-button" type="button" @click="handleCoverPhotoHelp">
                  <i class="fal fa-question-circle" />
                </button>
              </label>

              <div v-if="showCoverPhotoHelp" class="error-alert">
                <div />
                <p>
                  Max image size is <b>750 x 480 pixels.</b>
                </p>
              </div>

              <div class="input-group">
                <input
                  type="file"
                  class="input-field"
                  accept="image/*"
                  ref="coverPhoto"
                  @change="handlePhoto('coverPhoto', { width: 750, height: 480 })"
                >
                <span id='button1' class="input-field file-button" @click="triggerInputPhoto('coverPhoto')">
                  <span v-if="!hasCoverPhoto || coverPhoto">
                    {{ getPhotoName(coverPhoto) }}
                  </span>
                  <span v-else>
                    {{ teacher.teacher.fundraiser_main }}
                  </span>
                </span>
                <span class="span-placeholder">
                  <i class="fal fa-image" aria-hidden="true"></i>
                </span>
              </div>

            </div>
            <div class="input-group">
              <label>
                Upload your profile photo
                <button class="show-help-button" type="button" @click="handleProfilePhotoHelp">
                  <i class="fal fa-question-circle" />
                </button>
              </label>

              <div v-if="showProfilePhotoHelp" class="error-alert">
                <div />
                <p>
                  Max image size is <b>224 x 224 pixels.</b>
                </p>
              </div>

              <div class="input-group">
                <input
                  type="file"
                  accept="image/*"
                  ref="profilePhoto"
                  @change="handlePhoto('profilePhoto',{ width: 224, height: 224 })"
                >
                <span id='button2' class="input-field file-button" @click="triggerInputPhoto('profilePhoto')">
                  <span v-if="!hasProfilePhoto || profilePhoto">
                    {{ getPhotoName(profilePhoto) }}
                  </span>
                  <span v-else>
                    {{ teacher.teacher.profile_picture }}
                  </span>
                </span>
                <span class="span-placeholder">
                  <i class="fal fa-camera-alt" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-sm-6 mb-3">
          <a href="/teachers/information/school" class="btn btn-teacher-form btn-previous" :disabled="isSubmitting">
            PREVIOUS
          </a>
        </div>
        <div class="col-xs-12 col-sm-6 mb-3">
          <button type="submit" class="btn btn-teacher-form" :disabled="isSubmitting">
            <span v-show="!isSubmitting">
              CONTINUE
            </span>
            <span v-show="isSubmitting">
              <i class="fas fa-spin fa-spinner" />
              PROCESSING
            </span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Steps from './Steps.vue'
import { required } from 'vuelidate/lib/validators'
import WarningAlert from '../components/WarningAlert'
import TeacherService from '../../../services/TeacherService'

export default {
  name: 'TeachersPersonalImpactInformation',
  props: {
    teacher: Object,
    filledSteps: Array
  },
  components: {
    Steps,
    WarningAlert
  },
  data () {
    return {
      showCoverPhotoHelp: false,
      showProfilePhotoHelp: false,
      form: {
        titleStatement: null,
        statement: null,
        useImpactStatementMaterial: true
      },
      coverPhoto: null,
      profilePhoto: null,
      isAbort: false,
      isSubmitting: false
    }
  },
  created () {
    // eslint-disable-next-line camelcase
    const { teacher: { teacher_data } } = this.teacher

    if (!Array.isArray(this.teacher.teacher.teacher_data.personal_impact)) {
      this.form.titleStatement = teacher_data.personal_impact.titleStatement
      this.form.statement = teacher_data.personal_impact.statement
      this.form.useImpactStatementMaterial = teacher_data.personal_impact.useImpactStatementMaterial
    }
  },
  methods: {
    handleCoverPhotoHelp () {
      this.showCoverPhotoHelp = !this.showCoverPhotoHelp
    },
    handleProfilePhotoHelp () {
      this.showProfilePhotoHelp = !this.showProfilePhotoHelp
    },
    async handleSubmit () {
      // waitingAlert('Processing information, please wait ...')

      this.touchForm()

      if (!this.$v.form.$anyError && this.$v.form.$anyDirty && !this.isSubmitting) {
        this.isSubmitting = true
        const service = new TeacherService()

        if (this.coverPhoto || this.profilePhoto) {
          const files = {
            coverPhoto: this.coverPhoto,
            profilePhoto: this.profilePhoto
          }

          await service.fileSave(this.form, files, '/teachers/savePersonalImpactInformation')

          return null
        }

        await service.save(this.form, '/teachers/savePersonalImpactInformation')
      }
    },
    handlePhoto (refs, rules) {
      const file = this.$refs[refs].files[0]

      if (!file) {
        this.setPhoto(null, refs)
        return null
      }

      const FileBlob = new Blob([file], {
        name: file.name,
        type: file.type
      })

      this.isAbort = false

      // this.setPhoto(file, refs)

      const img = new Image()
      img.src = window.URL.createObjectURL(FileBlob)

      img.onload = () => {
        const width = img.naturalWidth
        const height = img.naturalHeight

        window.URL.revokeObjectURL(img.src)

        if (width > rules.width || height > rules.height) {
          this.isAbort = true
          refs === 'coverPhoto' ? this.showCoverPhotoHelp = true : this.showProfilePhotoHelp = true
          return null
        } else {
          this.setPhoto(file, refs)
        }
      }
    },
    setPhoto (file, refs) {
      if (this.isAbort) {
        return null
      }

      refs === 'coverPhoto'
        ? this.coverPhoto = file
        : this.profilePhoto = file
    },
    touchForm () {
      this.$v.form.statement.$touch()
      this.$v.form.titleStatement.$touch()
    },
    triggerInputPhoto (ref) {
      this.$refs[ref].click()
    },
    getPhotoName (file) {
      if (!file) {
        return 'Browse...'
      }

      return file.name
    }
  },
  validations: {
    form: {
      titleStatement: { required },
      statement: { required }
    }
  },
  computed: {
    hasCoverPhoto () {
      return !!this.teacher.teacher.fundraiser_main
    },
    hasProfilePhoto () {
      return !!this.teacher.teacher.profile_picture
    }
  }
}
</script>

<style lang="scss" scoped>
.partner-title {
  color: #6D6E71;
  font-size: 30px;
  margin-bottom: 45px;
}

.description {
  font-family: 'Roboto Slab';
  font-weight: bold;
  font-size: 22px;
  color: #999999;
  margin-bottom: 1.5rem;
}

.form-container {
  max-width: 750px;
  margin: 0 auto;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;

  .input-group {
    margin-bottom: 20px;
    display: block;

    label {
      margin-bottom: 10px;
      font-family: 'Rubik';
      font-weight: 300;
      font-size: 18px;
      color: #6d6e71;

      b {
        font-weight: 500;
      }
    }

    p {
      font-family: 'Rubik';
      font-weight: 300;
      color: #999999;
      line-height: 19px;
      font-size: 16px;
    }

    .input-field {
      font-weight: 300;
      font-size: 16px;
      font-family: 'Rubik';
      background-color: #F0F0F0;
      border-color: #6D6E71;
      border-width: 1px;
    }

    .input-field.success {
      border-color: #60B661;
    }

    .span-placeholder {
      position: absolute;
      top: 15px;
      left: 18px;
      font-size: 16px;
      color: #999999;
    }

    .span-state {
      position: absolute;
      top: 50px;
      right: 20px;
      font-size: 16px;
    }

    .input-radio {
      display: inline;
      position: relative;
      margin-right: 45px;
      font-family: 'Rubik';
      font-weight: 400;
      font-size: 18px;
      color: #6d6e71;
    }

    .d-block.mx-auto {
      display: block;
      margin: 0 auto;
      margin-bottom: 30px;
      text-align: center;
    }

    input[type=radio] {
      margin: 0 auto;
      border: 4px solid #6D6E71;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      transition: all ease .2s;

      &:hover {
        cursor: pointer;
      }

      &:checked {
        background-color: #FE8304;
        box-shadow: inset 0px 0px 0px 3px white;
        border: 4px solid #FE8304;
      }
    }
  }
}

.resource-center-alert {
  border: 1px solid #DDC5A4;
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: #FFF4E5;
  border-radius: 30px;
  margin-bottom: 2rem;

  font-family: 'Rubik';
  font-weight: 300;
  color: #663C00;
  font-size: 14px;
  line-height: 17px;

  p {
    margin-bottom: 1rem;
  }

  a  {
    color: #663C00;
    text-decoration: underline;
  }
}

.btn-teacher-form {
  width: 100%;
  max-width: 410px;
  margin: 0 auto;
  display: block;
  padding: .75rem;
  border: 2px solid #FD8204;
  color: #FD8204;
  font-size: 20px;
  font-weight: 500;
  background-color: #FFF;
  border-radius: 10px;
}

.btn-previous {
  border-color: #999999;
  color: #999999;
}

.mb-0 {
  margin-bottom: 0;
}

.error-alert {
  padding: 1rem;
  background-color: #FFF4E5;
  border: 1px solid #DDC5A4;
  border-radius: 15px;
  font-weight: 600;
  margin-top: 10px;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 15% 1fr;
  align-items: center;

  p {
    font-size: 14px !important;
    color: #663C00 !important;
  }

  span {
    position: relative !important;
    top: auto !important;
    color: #FFA12E !important;
    left: auto !important;
    text-align: center;
  }
}

.input-radio-group {
  display: grid;
  grid-template-columns: 10% 1fr;
  gap: 15px;

  input[type=checkbox] {
    margin: 0 auto;
    border: 4px solid #6D6E71;
    width: 20px;
    border-radius: 50%;
    transition: all ease .2s;

    &:hover {
      cursor: pointer;
    }

    &:checked {
      background-color: #6D6E71;
    }
  }

  span {
    font-family: 'Rubik';
    font-weight: 300;
    font-size: 18px;
    line-height: 22px;
    color: #6d6e71;
  }
}

textarea.input-field {
  height: 250px;
}

.pictures-grid {
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 15px;
  margin-bottom: 30px;
  gap: 30px;

  .input-field {
    padding-left: 45px;
  }

  input[type=file] {
    opacity: 0;
    &::-webkit-file-upload-button {
      visibility: hidden;
    }

    // &:before {
    //   content: 'Browse...';
    //   font-weight: 300;
    //   font-size: 16px;
    //   font-family: 'Rubik';
    //   background-color: #F0F0F0;
    //   border-color: #6D6E71;
    //   border-width: 1px;
    // }
  }

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
}

.show-help-button {
  background: transparent;
}

// .inner-circle {
//   display: block;
//   width: 8px;
//   height: 8px;
//   background: transparent;
//   border-radius: 10px;
//   position: absolute;
//   top: 2.5px;
//   left: 7px;
//   z-index: -99;
//   transition: all ease .2s;
// }

.filled-background {
  background: #FE8304;
}

span.input-field {
  width: 100%;
  display: block;
  bottom: 20px;
  position: relative;

  &:hover {
    cursor: pointer;
  }
}

span#button1 {
  bottom: 46px !important;
}
</style>



// WEBPACK FOOTER //
// resources/js/views/TeachersForm/Forms/PersonalImpactInformation.vue